"use client";

import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Modal } from "@/components/ui/Modal";
import MarkdownRenderer from "@/components/ui/MarkdownRenderer";
import Image from "next/image";

interface Addon {
  id: Id<"addons">;
  name: string;
  displayName: string;
  description: string;
  miniDescription?: string;
  pricing: {
    setupFee: number;
    monthlyFee: number;
  };
  features?: string[];
  logoImageId?: Id<"_storage">;
  imageIds?: Id<"_storage">[];
}

export default function AddonCard({ addon }: { addon: Addon }) {
  const [showModal, setShowModal] = useState(false);
  
  const logoUrl = useQuery(
    api.storage.getFileUrl,
    addon.logoImageId ? { storageId: addon.logoImageId as Id<"_storage"> } : "skip"
  );
  
  return (
    <>
      <div
        onClick={() => setShowModal(true)}
        className="border border-gray-200 bg-white rounded p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      >
        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center mb-3 overflow-hidden">
          {logoUrl ? (
            <Image
              src={logoUrl}
              alt={addon.displayName}
              width={40}
              height={40}
              className="object-contain"
            />
          ) : (
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          )}
        </div>
        <h3 className="font-poppins text-sm font-semibold text-gray-900 mb-2">
          {addon.displayName}
        </h3>
        <p className="font-poppins text-xs text-gray-600 mb-3">
          {addon.miniDescription || addon.description}
        </p>
        <div className="font-poppins text-xs text-gray-900 font-medium">
          ${(addon.pricing.setupFee / 100).toFixed(2)} setup + ${(addon.pricing.monthlyFee / 100).toFixed(2)}/mo
        </div>
      </div>
      
      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          size="xl"
          title={addon.displayName}
        >
          <div className="w-full">
            <div className="rounded h-80 flex items-center justify-center bg-gray-50">
              {addon.imageIds && addon.imageIds.length > 0 ? (
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-600 font-medium">Image Carousel</p>
                  <p className="text-gray-500 text-sm mt-1">{addon.imageIds.length} images</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-500 font-medium">No images available</p>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 relative">
            <div className="flex gap-6">
              <div className="w-2/3">
                <div className="prose prose-blue max-w-none">
                  {addon.description && (
                    <MarkdownRenderer content={addon.description} />
                  )}
                </div>
              </div>
              <div className="w-1/3">
                {/* Features Section */}
                {addon.features && addon.features.length > 0 && (
                  <div className="w-full">
                    <h4 className="font-poppins text-base font-semibold text-gray-900 mb-4">Key Features</h4>
                    <div className="flex flex-col gap-3">
                      {addon.features.map((feature: string, index: number) => (
                        <div key={index} className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="font-poppins text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <button className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900">
                  Contact Us to Get Started
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

